import React, { FC, PropsWithChildren, useEffect, useMemo, useRef } from 'react';

import { LengthRange } from '../../../../types/lengthRange';
import {
	IntervalDrawMouseDownFn,
	IntervalHandleResizeHandleMouseDownFn,
	IntervalLaneRowInterval,
} from '../configurations';

import { useIntervalLaneRowData } from './use-interval-lane-row-data';

export type IntervalsData = Record<string, IntervalData>;

export interface IntervalData {
	isLoading: boolean;
	intervals: IntervalLaneRowInterval[];
}

export interface IntervalLaneRowContextProps {
	handleIntervalResizeStarted: IntervalHandleResizeHandleMouseDownFn;
	handleIntervalResizeFinished: () => void;
	handleIntervalDrawStarted: IntervalDrawMouseDownFn;
	handleIntervalDrawFinished: () => void;
	intervalsData: IntervalsData;
	isEditingInterval: boolean;
	isDrawingInterval: boolean;
}

export const IntervalLaneRowContext = React.createContext<IntervalLaneRowContextProps>({
	handleIntervalResizeStarted: () => {},
	handleIntervalResizeFinished: () => {},
	handleIntervalDrawStarted: () => {},
	handleIntervalDrawFinished: () => {},
	intervalsData: {},
	isEditingInterval: false,
	isDrawingInterval: false,
});

export interface IntervalLaneRowProviderProps {
	mousePosition: number;
	allowableRanges?: LengthRange[];
	allowOverlapSetIds?: string[];
	maxOverlapCounts?: Record<string, number>;
}

export const IntervalLaneRowProvider: FC<PropsWithChildren<IntervalLaneRowProviderProps>> = (props) => {
	const context = useIntervalLaneRowData(props);
	const { handleIntervalResizeFinished, isEditingInterval, handleIntervalDrawFinished, isDrawingInterval } = context;
	const resizeRef = useRef<() => void>();
	const drawRef = useRef<() => void>();

	// Capture callback updates into a ref. We can call this from a separate delegate later which has its rendering deps different
	useEffect(() => {
		resizeRef.current = handleIntervalResizeFinished;
	}, [handleIntervalResizeFinished]);
	useEffect(() => {
		drawRef.current = handleIntervalDrawFinished;
	}, [handleIntervalDrawFinished]);

	// Only change the handler when the interval is editing. We use the ref to avoid constant re-binding of the event listener
	const resizeReleaseHandler = useMemo<(() => void) | undefined>(() => {
		if (isEditingInterval)
			return (): void => {
				const current = resizeRef.current;
				if (current) current();
			};
		return undefined;
	}, [isEditingInterval]);

	const drawReleaseHandler = useMemo<(() => void) | undefined>(() => {
		if (isDrawingInterval)
			return (): void => {
				const current = drawRef.current;
				if (current) current();
			};
		return undefined;
	}, [isDrawingInterval]);

	// Use the re-dep handler above, to only bind a handler when required, and to have render updates prevented with the ref
	useEffect(() => {
		const handler = resizeReleaseHandler;
		if (handler !== undefined) {
			addEventListener('mouseup', handler, { capture: true });
			//NOTE: Must specify capture (see: https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/removeEventListener)
			return (): void => removeEventListener('mouseup', handler, { capture: true });
		}
		return undefined;
	}, [resizeReleaseHandler]);

	useEffect(() => {
		const handler = drawReleaseHandler;
		if (handler !== undefined) {
			addEventListener('mouseup', handler, { capture: true });
			//NOTE: Must specify capture (see: https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/removeEventListener)
			return (): void => removeEventListener('mouseup', handler, { capture: true });
		}
		return undefined;
	}, [drawReleaseHandler]);

	return <IntervalLaneRowContext.Provider value={context}>{props.children}</IntervalLaneRowContext.Provider>;
};
